import React from "react";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FileText,
  Database,
} from "lucide-react";

export const buildFullPath = (item, parentPath = "") => {
  const current = item.name || "";
  if (parentPath) {
    return `${parentPath}/${current}`;
  }
  return current;
};

export const filterTree = (items = [], searchTerm) => {
  if (!searchTerm) return items;
  const lowerSearch = searchTerm.toLowerCase();
  return items
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterTree(item.children, searchTerm);
        if (
          filteredChildren.length > 0 ||
          item.name.toLowerCase().includes(lowerSearch)
        ) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      if (item.name.toLowerCase().includes(lowerSearch)) {
        return item;
      }
      return null;
    })
    .filter(Boolean);
};

export const getExpandedPaths = (items = [], searchTerm, parentPath = "") => {
  if (!searchTerm) return new Set();
  const expanded = new Set();
  const lowerSearch = searchTerm.toLowerCase();

  const traverse = (nodes, currentParent = "") => {
    nodes.forEach((node) => {
      const fullPath =
        node.full_path ||
        (currentParent ? `${currentParent}/${node.name}` : node.name);

      if (node.children) {
        const childExpanded = getExpandedPaths(
          node.children,
          searchTerm,
          fullPath
        );
        if (
          childExpanded.size > 0 ||
          node.name.toLowerCase().includes(lowerSearch)
        ) {
          expanded.add(fullPath);
          childExpanded.forEach((value) => expanded.add(value));
        }
      } else if (
        node.name.toLowerCase().includes(lowerSearch) &&
        currentParent
      ) {
        expanded.add(currentParent);
      }
    });
  };

  traverse(items, parentPath);
  return expanded;
};

const normalizePath = (path) => {
  if (!path) return "";
  return path.replace(/\./g, "/").replace(/\\/g, "/").toLowerCase().trim();
};

export const TreeItem = ({
  item,
  source,
  onSelect,
  selectedItems,
  level = 0,
  searchTerm,
  parentPath = "",
  expandedPaths = new Set(),
  isSelectable,
}) => {
  // fullPath
  const fullPath =
    item.full_path ||
    (parentPath ? buildFullPath(item, parentPath) : item.name);

  const shouldBeOpen = expandedPaths.has(fullPath);
  const [manualOpen, setManualOpen] = React.useState(false);
  const isSearching = !!searchTerm;
  const isOpen = isSearching ? shouldBeOpen : manualOpen;
  const selectable = isSelectable(item, source);

  const matchedSelectedItem = selectedItems.find((selected) => {
    if (selected.source !== source) return false;

    const itemPath = normalizePath(fullPath);
    const selectedPath = normalizePath(
      selected.full_path || selected.path
    );

    return (
      itemPath &&
      selectedPath &&
      (itemPath === selectedPath ||
        itemPath.endsWith(`/${selectedPath}`) ||
        selectedPath.endsWith(`/${itemPath}`))
    );
  });
  const isSelected = !!matchedSelectedItem;
  const handleSelect = (event) => {
    event.stopPropagation();
    if (!selectable) return;
    onSelect(isSelected ? matchedSelectedItem : 
      {
        path: fullPath,
        name: item.name,
        source,
        type: item.type || (item.children ? "folder" : "file"),
      },
      !isSelected
    );
  };

  const toggleOpen = () => {
    if (!item.children) return;
    if (!isSearching) {
      setManualOpen((prev) => !prev);
    }
  };

  if (
    searchTerm &&
    !item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!item.children || item.children.length === 0)
  ) {
    return null;
  }

  return (
    <div>
      <div
        className="flex items-center p-1.5 rounded-md cursor-pointer hover:bg-gray-100"
        style={{ paddingLeft: `${level * 20 + 8}px` }}
        onClick={toggleOpen}
      >
        {item.children ? (
          isOpen ? (
            <ChevronDown className="h-5" />
          ) : (
            <ChevronRight className="h-5" />
          )
        ) : (
          <div style={{ width: 20, minWidth: 20 }} />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginLeft: 1,
            marginRight: 1,
          }}
        >
          {selectable ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleSelect}
              onClick={(e) => e.stopPropagation()}
              className="h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          ) : (
            <span style={{ display: "inline-block", height: 16 }} />
          )}
        </div>

        {item.type === "table" || (!item.children && item.full_path) ? (
          <FileText className="h-5 mr-2 text-green-500" />
        ) : (source === "uc" || source === "uc_gcp") && item.children ? (
          <Database className="h-5 mr-2 text-blue-500" />
        ) : (
          <Folder className="h-5 mr-2 text-yellow-500" />
        )}

        <span className="truncate" title={item.name}>
          {item.name}
        </span>
      </div>

      {isOpen && item.children && (
        <div>
          {item.children.map((child, index) => (
            <TreeItem
              key={index}
              item={child}
              source={source}
              onSelect={onSelect}
              selectedItems={selectedItems}
              level={level + 1}
              searchTerm={searchTerm}
              parentPath={fullPath}
              expandedPaths={expandedPaths}
              isSelectable={isSelectable}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const getAllSelectableItems = (
  items = [],
  source,
  isSelectable,
  parentPath = ""
) => {
  const selectable = [];

  const traverse = (node, currentParentPath = "") => {
    const fullPath = node.full_path || buildFullPath(node, currentParentPath);
    if (isSelectable(node, source)) {
      selectable.push({
        path: fullPath,
        name: node.name,
        source,
        type: node.type || (node.children ? "folder" : "file"),
        full_path: fullPath,
      });
    }
    if (node.children) {
      node.children.forEach((child) => traverse(child, fullPath));
    }
  };

  // traversal (multi-schema)
  (items || []).forEach((item) =>
    traverse(item, parentPath ? parentPath : item.name)
  );

  return selectable;
};
